import React, { useState } from "react";

export default function ProductDetail({ product, onClose, onEnrich }) {
  const [enriching, setEnriching] = useState(false);
  const [enrichError, setEnrichError] = useState(null);

  if (!product) return null;

  const imgSrc = product.image || product.product_image;
  const shopUrl =
    product.shop_url ||
    `https://www.roughtrade.com/en-gb/product/${product.handle}`;

  // Group store availability by region
  const storeAvail = product.store_availability || [];
  const regionStores = {};
  storeAvail.forEach((s) => {
    const r = s.region || "Other";
    if (!regionStores[r]) regionStores[r] = [];
    regionStores[r].push(s);
  });

  const handleEnrich = async () => {
    setEnriching(true);
    setEnrichError(null);
    try {
      await onEnrich(product.id);
    } catch (err) {
      setEnrichError(err.response?.data?.error || "Enrichment failed");
    } finally {
      setEnriching(false);
    }
  };

  const isNotFound = product.discogs_id === -1;
  const canEnrich = product.barcode && (!product.discogs_id || isNotFound);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="detail-grid">
          {imgSrc ? (
            <img src={imgSrc} alt={product.title} className="detail-image" />
          ) : (
            <div
              className="detail-image"
              style={{ background: product.colour_swatch || "#222" }}
            />
          )}
          <div>
            <h2>{product.title}</h2>
            <p className="modal-artist">{product.product_type}</p>

            <div className="detail-badges">
              {product.source === "shopify_graphql" && (
                <span className="badge badge-source">SHOPIFY</span>
              )}
              {product.signed && (
                <span className="badge badge-signed">✍️ SIGNED</span>
              )}
              {product.exclusive && (
                <span className="badge badge-exclusive">EXCLUSIVE</span>
              )}
              {product.is_pre_order && (
                <span className="badge badge-preorder">PRE-ORDER</span>
              )}
              {product.is_on_sale && (
                <span className="badge badge-sale">ON SALE</span>
              )}
              {product.inventory_available ? (
                <span className="badge badge-instock">IN STOCK</span>
              ) : (
                <span className="badge badge-outofstock">OUT OF STOCK</span>
              )}
            </div>

            <div className="detail-section">
              <h3>Variant</h3>
              <div className="detail-row">
                <span className="label">Format</span>
                <span className="value">
                  {product.colour_swatch && (
                    <span
                      className="colour-dot"
                      style={{ backgroundColor: product.colour_swatch }}
                    />
                  )}
                  {product.variant_title || product.format_type}
                </span>
              </div>
              {product.colour_details && (
                <div className="detail-row">
                  <span className="label">Colour</span>
                  <span className="value">{product.colour_details}</span>
                </div>
              )}
              {product.format_details && (
                <div className="detail-row">
                  <span className="label">Format Details</span>
                  <span className="value">{product.format_details}</span>
                </div>
              )}
              {product.number_of_discs && (
                <div className="detail-row">
                  <span className="label">Discs</span>
                  <span className="value">{product.number_of_discs}</span>
                </div>
              )}
              {product.object_id && (
                <div className="detail-row">
                  <span className="label">Variant ID</span>
                  <span className="value">{product.object_id}</span>
                </div>
              )}
              {product.sku && (
                <div className="detail-row">
                  <span className="label">SKU</span>
                  <span className="value">{product.sku}</span>
                </div>
              )}
              {product.barcode && (
                <div className="detail-row">
                  <span className="label">Barcode</span>
                  <span className="value">{product.barcode}</span>
                </div>
              )}
              {product.customer_limit && (
                <div className="detail-row">
                  <span className="label">Limit</span>
                  <span className="value">
                    {product.customer_limit} per customer
                  </span>
                </div>
              )}
              {product.pre_order_limit && (
                <div className="detail-row">
                  <span className="label">Pre-order Limit</span>
                  <span className="value">{product.pre_order_limit}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3>Pricing</h3>
          <div className="detail-row">
            <span className="label">Price</span>
            <span className="value">
              {product.price_eur != null
                ? `€${Number(product.price_eur).toFixed(2)}`
                : "—"}
              {product.compare_at_price_eur && (
                <span
                  style={{
                    color: "#888",
                    textDecoration: "line-through",
                    marginLeft: 8,
                  }}
                >
                  €{Number(product.compare_at_price_eur).toFixed(2)}
                </span>
              )}
            </span>
          </div>
        </div>

        <div className="detail-section">
          <h3>Stock & Availability</h3>
          <div className="detail-row">
            <span className="label">Quantity</span>
            <span className="value">{product.inventory_quantity}</span>
          </div>
          <div className="detail-row">
            <span className="label">Regional Availability</span>
            <span className="value">
              <div className="region-stock">
                <span
                  className={`region-pill ${product.eu_in_stock ? "in-stock" : "out-of-stock"}`}
                >
                  EU {product.eu_in_stock ? "✓" : "✗"}
                </span>
                <span
                  className={`region-pill ${product.gb_in_stock ? "in-stock" : "out-of-stock"}`}
                >
                  GB {product.gb_in_stock ? "✓" : "✗"}
                </span>
                <span
                  className={`region-pill ${product.us_in_stock ? "in-stock" : "out-of-stock"}`}
                >
                  US {product.us_in_stock ? "✓" : "✗"}
                </span>
              </div>
            </span>
          </div>
          {(product.eu_dispatch_range ||
            product.gb_dispatch_range ||
            product.us_dispatch_range) && (
            <div className="detail-row">
              <span className="label">Dispatch (days)</span>
              <span className="value">
                <div className="region-stock">
                  {product.eu_dispatch_range && (
                    <span className="region-pill in-stock">
                      EU {product.eu_dispatch_range}
                    </span>
                  )}
                  {product.gb_dispatch_range && (
                    <span className="region-pill in-stock">
                      GB {product.gb_dispatch_range}
                    </span>
                  )}
                  {product.us_dispatch_range && (
                    <span className="region-pill in-stock">
                      US {product.us_dispatch_range}
                    </span>
                  )}
                </div>
              </span>
            </div>
          )}
          {storeAvail.length > 0 && (
            <div className="store-availability">
              <div className="detail-row" style={{ marginTop: 8 }}>
                <span className="label">Store Stock</span>
              </div>
              <div className="store-grid">
                {storeAvail.map((s, i) => (
                  <div
                    key={i}
                    className={`store-item ${s.available ? "available" : "unavailable"}`}
                  >
                    <span className="store-name">{s.name}</span>
                    <span className="store-region">{s.region}</span>
                    <span className="store-qty">
                      {s.available ? (s.quantity > 0 ? s.quantity : "✓") : "—"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {(product.catalogue_number_eu ||
          product.catalogue_number_gb ||
          product.catalogue_number_us) && (
          <div className="detail-section">
            <h3>Catalogue Numbers</h3>
            {product.catalogue_number_eu && (
              <div className="detail-row">
                <span className="label">EU</span>
                <span className="value">{product.catalogue_number_eu}</span>
              </div>
            )}
            {product.catalogue_number_gb && (
              <div className="detail-row">
                <span className="label">GB</span>
                <span className="value">{product.catalogue_number_gb}</span>
              </div>
            )}
            {product.catalogue_number_us && (
              <div className="detail-row">
                <span className="label">US</span>
                <span className="value">{product.catalogue_number_us}</span>
              </div>
            )}
          </div>
        )}

        <div className="detail-section">
          <h3>Release Info</h3>
          <div className="detail-row">
            <span className="label">Release Date</span>
            <span className="value">{product.release_date || "—"}</span>
          </div>
          {product.genre && (
            <div className="detail-row">
              <span className="label">Genre</span>
              <span className="value">{product.genre}</span>
            </div>
          )}
          {product.label_name && (
            <div className="detail-row">
              <span className="label">Label</span>
              <span className="value">{product.label_name}</span>
            </div>
          )}
          {product.vendor && (
            <div className="detail-row">
              <span className="label">Vendor</span>
              <span className="value">{product.vendor}</span>
            </div>
          )}
        </div>

        {product.body_html && (
          <div className="detail-section">
            <h3>Description</h3>
            <div
              className="detail-description"
              dangerouslySetInnerHTML={{ __html: product.body_html }}
            />
          </div>
        )}

        {product.discogs_id && product.discogs_id > 0 && (
          <div className="detail-section discogs-section">
            <h3>
              <span className="discogs-logo">💿</span> Discogs Market Data
            </h3>
            <div className="detail-row">
              <span className="label">Hotness Score</span>
              <span className="value">
                {product.discogs_hotness_score != null ? (
                  <span className="hotness-value">
                    {product.discogs_hotness_score.toFixed(1)}
                    <span
                      className="hotness-indicator"
                      style={{
                        background: `hsl(${Math.round((Math.min(100, product.discogs_hotness_score) / 100) * 120)}, 70%, 50%)`,
                      }}
                    />
                  </span>
                ) : (
                  "—"
                )}
              </span>
            </div>
            <div className="detail-row">
              <span className="label">Lowest Price</span>
              <span className="value">
                {product.discogs_lowest_price != null
                  ? `€${Number(product.discogs_lowest_price).toFixed(2)}`
                  : "—"}
                {product.discogs_num_for_sale > 0 && (
                  <span style={{ color: "#888", marginLeft: 8 }}>
                    ({product.discogs_num_for_sale} for sale)
                  </span>
                )}
              </span>
            </div>
            {product.discogs_avg_sold_price && (
              <div className="detail-row">
                <span className="label">Avg Sold Price</span>
                <span className="value">
                  €{Number(product.discogs_avg_sold_price).toFixed(2)}
                </span>
              </div>
            )}
            {product.discogs_last_sold_price && (
              <div className="detail-row">
                <span className="label">Last Sold</span>
                <span className="value">
                  €{Number(product.discogs_last_sold_price).toFixed(2)}
                  {product.discogs_last_sold_date && (
                    <span style={{ color: "#888", marginLeft: 8 }}>
                      ({product.discogs_last_sold_date})
                    </span>
                  )}
                </span>
              </div>
            )}
            <div className="detail-row">
              <span className="label">Community</span>
              <span className="value">
                <span style={{ color: "#4ade80" }}>
                  {product.discogs_community_have || 0} have
                </span>
                {" / "}
                <span style={{ color: "#f59e0b" }}>
                  {product.discogs_community_want || 0} want
                </span>
              </span>
            </div>
            {product.discogs_url && (
              <div className="detail-row">
                <span className="label">Link</span>
                <span className="value">
                  <a
                    href={product.discogs_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#60a5fa" }}
                  >
                    View on Discogs →
                  </a>
                </span>
              </div>
            )}
          </div>
        )}

        <div className="detail-actions">
          {canEnrich && (
            <button
              className="btn btn-discogs"
              onClick={handleEnrich}
              disabled={enriching}
            >
              {enriching
                ? "Enriching..."
                : isNotFound
                  ? "🔄 Retry Discogs Enrichment"
                  : "💿 Enrich from Discogs"}
            </button>
          )}
          {isNotFound && !enriching && (
            <span style={{ color: "#f59e0b", fontSize: "0.85rem" }}>
              Not found on Discogs
            </span>
          )}
          {enrichError && (
            <span style={{ color: "#f87171", fontSize: "0.85rem" }}>
              {enrichError}
            </span>
          )}
          <a
            href={shopUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="detail-link"
          >
            🛒 View on Rough Trade
          </a>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
